import { Controller } from '@hotwired/stimulus';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';

export default class ContractController extends Controller {
  static targets = ['contractContainer', 'contractIframe'];

  static values = {
    subscriptionId: Number,
  };

  connect() {
    this.pdfDoc = null;
    this.scale = this.calculateScale();
    this.canvases = [];

    this.element.addEventListener(
      'signature:load-contract',
      this.handleLoadContract.bind(this)
    );
    this.element.addEventListener('resize', this.handleResize.bind(this));

    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
  }

  disconnect() {
    this.element.removeEventListener(
      'signature:load-contract',
      this.handleLoadContract.bind(this)
    );
    this.element.removeEventListener('resize', this.handleResize.bind(this));
  }

  handleLoadContract(event) {
    this.loadContractUrl();
  }

  loadContractUrl() {
    if (!this.hasSubscriptionIdValue) {
      return;
    }

    fetch(`/investissements/${this.subscriptionIdValue}/contract_url`, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.contract_url) {
          this.generateContractIframe(data.contract_url);
        }
      })
      .catch((error) => {
        this.dispatch('contract-load-error', { detail: { error } });
      });
  }

  generateContractIframe(documentUrl) {
    if (!this.hasContractContainerTarget) {
      return;
    }

    this.contractContainerTarget.innerHTML = '';
    this.contractContainerTarget.style.overflowY = 'auto';
    this.contractContainerTarget.style.maxHeight = '80vh';
    this.canvases = [];

    const renderPage = (pageNum) => {
      return this.pdfDoc.getPage(pageNum).then((page) => {
        let containerWidth = window.innerWidth - 32;

        const computedWidth =
          this.contractContainerTarget.clientWidth ||
          this.contractContainerTarget.offsetWidth;
        if (computedWidth > 0) {
          containerWidth = computedWidth - 32;
        }

        const maxWidth = Math.max(containerWidth, 300);
        let scale = this.scale;

        let viewport = page.getViewport({ scale: scale });

        if (viewport.width > maxWidth) {
          scale = maxWidth / (viewport.width / scale);
          viewport = page.getViewport({ scale: scale });
        }

        const canvas = document.createElement('canvas');

        canvas.height = viewport.height * 2;
        canvas.width = viewport.width * 2;
        canvas.style.width = viewport.width + 'px';
        canvas.style.maxWidth = '100%';
        canvas.style.height = 'auto';
        canvas.style.display = 'block';
        canvas.style.marginBottom = '8px';
        canvas.style.marginInline = 'auto';

        const ctx = canvas.getContext('2d');
        ctx.scale(2, 2)

        const renderContext = {
          canvasContext: ctx,
          viewport: viewport,
        };

        this.contractContainerTarget.appendChild(canvas);
        this.canvases.push(canvas);

        return page.render(renderContext).promise;
      });
    };

    this.renderAllPages = () => {
      if (!this.pdfDoc) return;

      this.contractContainerTarget.innerHTML = '';
      this.canvases = [];

      const renderPromises = [];
      for (let i = 1; i <= this.pdfDoc.numPages; i++) {
        renderPromises.push(renderPage(i));
      }

      return Promise.all(renderPromises);
    };

    pdfjsLib.getDocument(documentUrl).promise.then((pdfDoc_) => {
      this.pdfDoc = pdfDoc_;
      this.renderAllPages();
    });
  }

  calculateScale() {
    const isMobile = window.innerWidth <= 768;

    if (!this.hasContractContainerTarget) {
      return 1.5
    }

    const containerWidth =
      this.contractContainerTarget.clientWidth ||
      this.contractContainerTarget.offsetWidth ||
      window.innerWidth;

    if (isMobile) {
      return Math.min(2.0, Math.max(1.2, containerWidth / 400));
    }

    return 1.5;
  }

  handleResize() {
    this.scale = this.calculateScale();
    if (this.pdfDoc) {
      this.renderAllPages();
    }
  }

  dispatch(eventName, options = {}) {
    const event = new CustomEvent(`signature:${eventName}`, {
      bubbles: true,
      detail: options.detail || {},
    });
    this.element.dispatchEvent(event);
  }
}
